/**
 * Restyles Judge.me preview badges:
 * - PDP: rating-header chrome, count copy, scroll to #pdp-buy-zone-reviews
 * - Product cards: compact count + star pill (no click hijack; card link wins)
 *
 * Card/PDP count labels come from Liquid locale strings via
 * `data-reviews-label-template` (placeholder `[[count]]`).
 *
 * Judge.me often rewrites badge text after load; we re-apply the locale label
 * whenever the count text drifts from our template.
 */
const REVIEWS_ANCHOR = '#pdp-buy-zone-reviews';

/**
 * @param {number} score
 * @returns {string}
 */
function starsFromScore(score) {
  const rounded = Math.max(0, Math.min(5, Math.round(Number(score) || 0)));
  return `${'★'.repeat(rounded)}${'☆'.repeat(5 - rounded)}`;
}

/**
 * @param {HTMLElement} badge
 * @returns {string | undefined}
 */
function reviewsLabelTemplate(badge) {
  const fromBadge = badge.dataset.reviewsLabelTemplate;
  if (fromBadge) return fromBadge;

  const host = badge.closest('[data-reviews-label-template]');
  if (host instanceof HTMLElement && host.dataset.reviewsLabelTemplate) {
    return host.dataset.reviewsLabelTemplate;
  }

  return undefined;
}

/**
 * @param {number|string} count
 * @param {'pdp' | 'card'} context
 * @param {HTMLElement} badge
 * @returns {string}
 */
function reviewsCountLabel(count, context, badge) {
  const n = Number(count) || 0;
  const template = reviewsLabelTemplate(badge);

  if (template) {
    return template.replaceAll('[[count]]', String(n));
  }

  if (context === 'card') {
    return String(n);
  }

  return `(${n})`;
}

/**
 * @param {HTMLElement} badge
 * @returns {'pdp' | 'card'}
 */
function badgeContext(badge) {
  if (badge.closest('.product-card-sana')) return 'card';
  return 'pdp';
}

/**
 * Keep stars + locale count in sync (safe to call repeatedly).
 * @param {HTMLElement} badge
 */
function applyJudgeMeBadgeContent(badge) {
  const context = badgeContext(badge);
  const score = badge.dataset.averageRating || badge.querySelector('[data-score]')?.getAttribute('data-score');
  const count = badge.dataset.numberOfReviews;
  const starsEl = badge.querySelector('.jdgm-prev-badge__stars');
  const textEl = badge.querySelector('.jdgm-prev-badge__text');

  if (starsEl && score != null) {
    const desiredStars = starsFromScore(score);
    if (starsEl.textContent !== desiredStars) {
      starsEl.textContent = desiredStars;
    }
    starsEl.setAttribute('aria-hidden', 'true');
  }

  if (textEl && count != null) {
    const desiredLabel = reviewsCountLabel(count, context, badge);
    if (textEl.textContent !== desiredLabel) {
      textEl.textContent = desiredLabel;
    }
  }

  return { context, starsEl, textEl };
}

/**
 * @param {HTMLElement} badge
 */
function enhanceJudgeMeBadge(badge) {
  const { context, starsEl, textEl } = applyJudgeMeBadgeContent(badge);

  if (badge.dataset.jdgmStyled === 'true') {
    return;
  }

  const labelStars = starsEl?.textContent || '';
  const labelCount = textEl?.textContent || '';
  const accessibleName = [labelStars, labelCount].filter(Boolean).join(', ');

  badge.dataset.jdgmStyled = 'true';
  badge.dataset.pdpStyled = 'true';
  badge.classList.add(context === 'card' ? 'card-jdgm-badge' : 'pdp-jdgm-badge');

  const widget = badge.closest('.jdgm-preview-badge');
  if (widget instanceof HTMLElement) {
    widget.classList.add(context === 'card' ? 'card-jdgm-badge-widget' : 'pdp-jdgm-badge-widget');
  }

  if (context === 'card') {
    if (accessibleName && widget instanceof HTMLElement) {
      widget.setAttribute('aria-label', accessibleName);
    }
    return;
  }

  const linkHost = widget instanceof HTMLElement ? widget : badge;
  linkHost.setAttribute('role', 'link');
  linkHost.setAttribute('tabindex', '0');
  if (accessibleName) {
    linkHost.setAttribute('aria-label', accessibleName);
  }

  const goToReviews = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const target = document.querySelector(REVIEWS_ANCHOR);
    if (target instanceof HTMLElement) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
      target.focus({ preventScroll: true });
    } else {
      window.location.hash = REVIEWS_ANCHOR;
    }
  };

  linkHost.addEventListener('click', goToReviews);
  linkHost.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      goToReviews(event);
    }
  });
}

/**
 * @param {Document|Element} [root]
 */
function scanJudgeMeBadges(root = document) {
  for (const badge of root.querySelectorAll('.jdgm-preview-badge .jdgm-prev-badge, .jdgm-prev-badge')) {
    if (badge instanceof HTMLElement) {
      enhanceJudgeMeBadge(badge);
    }
  }
}

/**
 * @param {Node} node
 * @returns {HTMLElement | null}
 */
function nearestBadge(node) {
  const el = node instanceof HTMLElement ? node : node.parentElement;
  if (!(el instanceof HTMLElement)) return null;

  if (el.matches('.jdgm-prev-badge')) return el;
  const nested = el.closest('.jdgm-prev-badge');
  if (nested instanceof HTMLElement) return nested;
  if (el.matches('.jdgm-preview-badge') || el.closest('.jdgm-preview-badge')) {
    const badge = el.querySelector('.jdgm-prev-badge') || el.closest('.jdgm-preview-badge')?.querySelector('.jdgm-prev-badge');
    return badge instanceof HTMLElement ? badge : null;
  }
  return null;
}

scanJudgeMeBadges();

const observer = new MutationObserver((mutations) => {
  /** @type {Set<HTMLElement>} */
  const badges = new Set();

  for (const mutation of mutations) {
    if (mutation.type === 'characterData') {
      const badge = nearestBadge(mutation.target);
      if (badge) badges.add(badge);
      continue;
    }

    if (mutation.type === 'attributes') {
      const badge = nearestBadge(mutation.target);
      if (badge) badges.add(badge);
      continue;
    }

    for (const node of mutation.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;

      if (node.matches?.('.jdgm-prev-badge')) {
        badges.add(node);
      } else if (node.matches?.('.jdgm-preview-badge')) {
        const badge = node.querySelector('.jdgm-prev-badge');
        if (badge instanceof HTMLElement) badges.add(badge);
      } else {
        for (const badge of node.querySelectorAll?.('.jdgm-prev-badge') || []) {
          if (badge instanceof HTMLElement) badges.add(badge);
        }
      }
    }

    if (mutation.type === 'childList' && mutation.target instanceof HTMLElement) {
      const badge = nearestBadge(mutation.target);
      if (badge) badges.add(badge);
    }
  }

  for (const badge of badges) {
    enhanceJudgeMeBadge(badge);
  }
});

observer.observe(document.documentElement, {
  childList: true,
  subtree: true,
  characterData: true,
  attributes: true,
  attributeFilter: ['data-number-of-reviews', 'data-average-rating'],
});
